import React, { useState, useEffect } from 'react';
import { X, Star, CheckCircle2, Loader2, Send } from 'lucide-react';
import { supabase } from '../services/supabase';
import { captureEvent } from '../providers/PostHogProvider';
import type { Organization } from '../types';

const COOLDOWN_DAYS = 60;
const MIN_CREDITS = 2;
const SHOW_DELAY_MS = 5000;

function storageKey(orgId: string) {
  return `sideoutiq_csat_last_shown_${orgId}`;
}

function isEligible(org: Organization): boolean {
  if (org.isBetaTester) return false;
  if ((org.matchCreditsUsed ?? 0) < MIN_CREDITS) return false;
  const raw = localStorage.getItem(storageKey(org.id));
  if (!raw) return true;
  return (Date.now() - Number(raw)) / 86_400_000 >= COOLDOWN_DAYS;
}

interface Props {
  org: Organization;
  userId: string;
  userEmail?: string;
}

export default function CsatBanner({ org, userId, userEmail }: Props) {
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const [favorite, setFavorite] = useState('');
  const [improvement, setImprovement] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEligible(org)) return;
    const timer = setTimeout(() => {
      localStorage.setItem(storageKey(org.id), String(Date.now()));
      setVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnimateIn(true)));
    }, SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [org.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const slideOut = () => {
    setAnimateIn(false);
    setTimeout(() => setVisible(false), 300);
  };

  const handleSubmit = async () => {
    if (!rating || submitting) return;
    setSubmitting(true);
    setError(null);

    const { error: dbError } = await supabase.from('csat_responses').insert({
      organization_id: org.id,
      user_id: userId,
      rating,
      favorite_feature: favorite.trim() || null,
      improvement: improvement.trim() || null,
    });

    if (dbError) {
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
      return;
    }

    captureEvent('csat_submitted', {
      rating,
      has_favorite: !!favorite.trim(),
      has_improvement: !!improvement.trim(),
      org_id: org.id,
    });

    // Notify admin — soft fail so the user is never blocked
    fetch('/api/notify-csat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userEmail,
        orgName: org.name,
        rating,
        favoriteFeature: favorite.trim() || null,
        improvement: improvement.trim() || null,
      }),
    }).catch(e => console.error('notify-csat error:', e));

    setDone(true);
    setTimeout(slideOut, 2000);
  };

  if (!visible) return null;

  const activeRating = hovered ?? rating;

  return (
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-full max-w-sm px-4 pointer-events-none transition-all duration-300 ease-out ${
        animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="pointer-events-auto bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden">
        {done ? (
          <div className="flex flex-col items-center gap-2 py-6 px-5 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
            <p className="font-bold text-slate-800">Thanks for the feedback!</p>
            <p className="text-sm text-slate-500">We read every response.</p>
          </div>
        ) : (
          <div className="p-5">
            <div className="flex items-start justify-between gap-2 mb-4">
              <p className="font-bold text-slate-800 text-base leading-snug">
                How are you liking SideoutIQ?
              </p>
              <button
                type="button"
                onClick={slideOut}
                className="shrink-0 p-1 text-slate-400 hover:text-slate-600 rounded-lg transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div
              className="flex gap-1 mb-4"
              onMouseLeave={() => setHovered(null)}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHovered(n)}
                  className="flex-1 flex items-center justify-center py-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                  aria-label={`${n} star${n !== 1 ? 's' : ''}`}
                >
                  <Star
                    className={`w-7 h-7 transition-colors ${
                      n <= (activeRating ?? 0)
                        ? 'text-orange-500 fill-orange-500'
                        : 'text-slate-300'
                    }`}
                    strokeWidth={1.5}
                  />
                </button>
              ))}
            </div>

            {rating !== null && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">
                    What's your favorite part?
                  </label>
                  <textarea
                    value={favorite}
                    onChange={(e) => setFavorite(e.target.value.slice(0, 300))}
                    placeholder="e.g. live rotation tracking"
                    rows={2}
                    autoFocus
                    className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-slate-700 placeholder:text-slate-400 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1.5">
                    One thing you'd improve or add?
                  </label>
                  <textarea
                    value={improvement}
                    onChange={(e) => setImprovement(e.target.value.slice(0, 300))}
                    placeholder="e.g. export stats to CSV"
                    rows={2}
                    className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-slate-700 placeholder:text-slate-400 transition-all"
                  />
                </div>
                {error && (
                  <p className="text-xs text-red-600 font-medium">{error}</p>
                )}
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 rounded-xl text-sm transition-all active:scale-[0.98] disabled:opacity-60 shadow-md shadow-blue-600/20"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            )}

            {rating === null && (
              <button
                type="button"
                onClick={slideOut}
                className="w-full text-center text-xs text-slate-400 hover:text-slate-600 py-1 transition-colors"
              >
                Maybe later
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
